// src/pages/TrainingDetail.js
import React, { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import {
  Code2, Smartphone, Layers, Search, Server, Cloud, Sparkles, MessageSquare, Bot,
  GraduationCap, Clock, Award, ArrowRight, CheckCircle2, ArrowLeft, Download, Target, BookOpen
} from 'lucide-react';
import SEO from '../components/SEO';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import TrainingEnrollForm from '../components/TrainingEnrollForm';
import { getProgramBySlug, getRelatedPrograms, eligibleDegrees } from '../data/trainingData';
import '../styles/TrainingDetail.css';

const ICONS = { Code2, Smartphone, Layers, Search, Server, Cloud, Sparkles, MessageSquare, Bot };

const TrainingDetail = () => {
  const { slug } = useParams();
  const program = getProgramBySlug(slug);
  const [activeRoadmap, setActiveRoadmap] = useState('sixWeeks');
  const [trackChoice, setTrackChoice] = useState(program?.trackOptions?.[0]);

  if (!program) {
    return <Navigate to="/training" replace />;
  }

  const Icon = ICONS[program.icon];
  const relatedPrograms = getRelatedPrograms(slug, 3);
  const roadmapStages = program.roadmap ? program.roadmap[activeRoadmap] : null;
  const hasTrackChoice = Boolean(program.trackOptions && program.trackOptions.length > 1);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: program.title,
    description: program.seo.description,
    provider: {
      '@type': 'Organization',
      name: 'EDSY Tech',
      sameAs: 'https://edsytech.com'
    },
    hasCourseInstance: program.durations.map((duration) => ({
      '@type': 'CourseInstance',
      courseMode: 'Blended',
      courseWorkload: duration
    }))
  };

  return (
    <div className="training-detail-page">
      <SEO
        title={program.seo.title}
        description={program.seo.description}
        canonicalPath={`/training/${program.slug}`}
        keywords={program.seo.keywords}
        jsonLd={jsonLd}
      />
      <Navbar />

      <section className="td-hero">
        <div className="td-hero-container">
          <Link to="/training" className="td-breadcrumb">
            <ArrowLeft className="td-breadcrumb-icon" />
            All Training Programs
          </Link>
          <div className="td-hero-icon-wrapper">
            {Icon && <Icon className="td-hero-icon" />}
          </div>
          <span className="td-hero-category">{program.category}</span>
          <h1 className="td-hero-title">{program.title}</h1>
          <p className="td-hero-tagline">{program.tagline}</p>
          <div className="td-hero-meta">
            <span className="td-hero-meta-item">
              <Clock className="td-hero-meta-icon" />
              {program.durations.join(' or ')}
            </span>
            <span className="td-hero-meta-item">
              <GraduationCap className="td-hero-meta-icon" />
              {eligibleDegrees.join(', ')}
            </span>
            <span className="td-hero-meta-item">
              <Award className="td-hero-meta-icon" />
              Certificate on completion
            </span>
          </div>
          <div className="td-hero-actions">
            <a href="#apply" className="td-hero-cta">
              Apply for This Program
              <ArrowRight className="td-hero-cta-icon" />
            </a>
            {program.trainingGuideUrl && (
              <Link to={`/training/${program.slug}/guide`} className="td-hero-cta-secondary">
                <BookOpen className="td-hero-cta-icon" />
                Read Training Guide
              </Link>
            )}
            {program.trainingGuidePdfUrl && (
              <a
                href={program.trainingGuidePdfUrl}
                className="td-hero-cta-secondary"
                target="_blank"
                rel="noopener noreferrer"
                download
              >
                <Download className="td-hero-cta-icon" />
                Download PDF
              </a>
            )}
          </div>
        </div>
      </section>

      <section className="td-body">
        <div className="td-body-container">
          <div className="td-main">
            <div className="td-block">
              <h2 className="td-block-title">About This Program</h2>
              <p className="td-description">{program.description}</p>
            </div>

            {program.scope && (
              <div className="td-block">
                <h2 className="td-block-title">Scope</h2>
                <p className="td-description">{program.scope}</p>
              </div>
            )}

            {roadmapStages && (
              <div className="td-block">
                <div className="td-roadmap-header">
                  <h2 className="td-block-title">Training Roadmap</h2>
                  <div className="td-roadmap-toggle">
                    <button
                      type="button"
                      className={`td-roadmap-toggle-btn ${activeRoadmap === 'sixWeeks' ? 'td-roadmap-toggle-btn-active' : ''}`}
                      onClick={() => setActiveRoadmap('sixWeeks')}
                    >
                      6 Weeks
                    </button>
                    <button
                      type="button"
                      className={`td-roadmap-toggle-btn ${activeRoadmap === 'sixMonths' ? 'td-roadmap-toggle-btn-active' : ''}`}
                      onClick={() => setActiveRoadmap('sixMonths')}
                    >
                      6 Months
                    </button>
                  </div>
                </div>

                {hasTrackChoice && (
                  <div className="td-frontend-choice">
                    <span className="td-frontend-choice-label">{program.trackLabel || 'Track'}:</span>
                    <div className="td-roadmap-toggle">
                      {program.trackOptions.map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`td-roadmap-toggle-btn ${trackChoice === option ? 'td-roadmap-toggle-btn-active' : ''}`}
                          onClick={() => setTrackChoice(option)}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="td-roadmap-timeline">
                  {roadmapStages.map((stage, index) => {
                    const resolved = stage.variants ? stage.variants[trackChoice] : stage;
                    return (
                      <div key={stage.period} className="td-roadmap-stage">
                        <div className="td-roadmap-stage-marker">
                          <span className="td-roadmap-stage-number">{index + 1}</span>
                        </div>
                        <div className="td-roadmap-stage-content">
                          <span className="td-roadmap-stage-period">{stage.period}</span>
                          <h3 className="td-roadmap-stage-title">{stage.title}</h3>
                          <p className="td-roadmap-stage-scope">{stage.scope}</p>
                          <div className="td-roadmap-stage-stack">
                            {resolved.stack.map((tech) => (
                              <span key={tech} className="td-roadmap-stack-pill">{tech}</span>
                            ))}
                          </div>
                          <ul className="td-roadmap-stage-topics">
                            {resolved.topics.map((topic) => (
                              <li key={topic}>
                                <Target className="td-roadmap-topic-icon" />
                                <span>{topic}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {!roadmapStages && (
              <div className="td-block">
                <h2 className="td-block-title">Curriculum</h2>
                <div className="td-curriculum-grid">
                  <div className="td-curriculum-card">
                    <h3 className="td-curriculum-card-title">6 Weeks &mdash; Industrial Training</h3>
                    <ul className="td-curriculum-list">
                      {program.curriculum.sixWeeks.map((item) => (
                        <li key={item}>
                          <CheckCircle2 className="td-curriculum-icon" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="td-curriculum-card td-curriculum-card-highlight">
                    <h3 className="td-curriculum-card-title">6 Months &mdash; Internship</h3>
                    <ul className="td-curriculum-list">
                      {program.curriculum.sixMonths.map((item) => (
                        <li key={item}>
                          <CheckCircle2 className="td-curriculum-icon" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            <div className="td-block">
              <h2 className="td-block-title">What You&rsquo;ll Walk Away With</h2>
              <ul className="td-outcomes-list">
                {program.outcomes.map((outcome) => (
                  <li key={outcome} className="td-outcomes-item">
                    <CheckCircle2 className="td-curriculum-icon" />
                    <span>{outcome}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="td-block">
              <h2 className="td-block-title">Tools & Technologies</h2>
              <div className="td-tools-list">
                {program.tools.map((tool) => (
                  <span key={tool} className="td-tool-pill">{tool}</span>
                ))}
              </div>
            </div>

            <div className="td-block td-eligibility-block">
              <h2 className="td-block-title">Eligibility</h2>
              <p className="td-description">
                Open to current students and recent graduates of {eligibleDegrees.join(', ')}, or equivalent
                computer science / IT degrees. No prior professional experience required.
              </p>
            </div>
          </div>

          <div className="td-sidebar">
            <div id="apply" className="td-sidebar-sticky">
              <TrainingEnrollForm defaultProgram={program.title} />
            </div>
          </div>
        </div>
      </section>

      {relatedPrograms.length > 0 && (
        <section className="td-related-section">
          <div className="td-related-container">
            <h2 className="td-block-title">Other Programs You Might Like</h2>
            <div className="td-related-grid">
              {relatedPrograms.map((related) => {
                const RelatedIcon = ICONS[related.icon];
                return (
                  <Link key={related.slug} to={`/training/${related.slug}`} className="td-related-card">
                    <div className="td-related-icon-wrapper">
                      {RelatedIcon && <RelatedIcon className="td-related-icon" />}
                    </div>
                    <h3 className="td-related-title">{related.title}</h3>
                    <p className="td-related-tagline">{related.tagline}</p>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default TrainingDetail;
